import type { CaseDetails, CodedValue, EnterpriseImage, EnterpriseImageDetails, PatientDetails } from '../../features/upload/api';
import { EMPTY_DRAFT, splitPn, type MetadataDraft } from '../../features/upload/sidecar';
import { imageNameOf } from '../../features/upload/imageName';
import { certainKind, defaultImageType, imageTypeOf, isImageType, isLaterality, lateralityOfCode, typeFits, type ImageKind, type ImageType } from '../../data/imageTypes';

/**
 * The enterprise upload form, as the server takes it (server/src/http/drafts.ts
 * imageColumns and studyWrite).
 *
 * The form is one string per field; the server takes an IMAGE body (PATCH
 * /images/:id) and a STUDY body (PATCH /images/:id/study). Each is split into
 * GROUPS, the unit a save is measured in: most groups are one key, but two are
 * several keys that must travel together —
 *
 *   accession  number and issuer: the server reads the issuer only beside its
 *              number, and a number sent alone clears the issuer.
 *   patient    id, issuer, name and birth date: the patient is linked as a
 *              whole, and a patient sent without its name clears the name.
 *
 * A save sends only the groups that differ from what the server last took, so
 * a value the server has that the form never showed is never overwritten by a
 * field that merely says nothing.
 */

export type FileKind = 'wsi' | 'flat' | 'document';

const WSI_EXT = new Set(['svs', 'ndpi', 'tif', 'tiff', 'scn', 'dcm', 'zip', 'tar', 'mrxs']);

/** A slide, a flat image (a photo) or a document (a PDF), by extension: the same rule as the community uploader. */
export function kindOf(name: string): FileKind {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  if (ext === 'pdf') return 'document';
  return WSI_EXT.has(ext) ? 'wsi' : 'flat';
}

/**
 * What a row's file certainly is, or null until the converter says
 * (data/imageTypes.ts certainKind): a scanner's format is a slide, and so is
 * a MIRAX slide the page packed (its members hold the .mrxs); a packed slide
 * of DICOM files is not told apart from a flat DICOM picture here.
 */
export const certainKindOf = (r: { name: string; members?: ReadonlyArray<{ name: string }> }): ImageKind | null =>
  r.members ? (r.members.some((m) => /\.mrxs$/i.test(m.name)) ? 'wsi' : null) : certainKind(r.name);

/**
 * The form a new row starts from: the Type of its kind (a slide is a slide,
 * a photo is gross; the server writes the modality from it) and the specimen
 * type the server writes to every web upload unless told otherwise
 * (WEB_IMAGE_DEFAULTS), so the form shows what the image will say. The
 * illumination is the Type's (immunofluorescence lights its own) and is not
 * the form's to say. Given its `file` (a new row's), its Name is the file's
 * (imageNameOf); a DICOM header's or a metadata file's series description
 * is merged over it, and a Specimen ID names it while its Name is still a
 * default (imageName.ts followName, in the store).
 */
export function initialDraft(kind: FileKind, file?: { name: string; slide?: boolean }, certainType: ImageType | null = null): MetadataDraft {
  return {
    ...EMPTY_DRAFT,
    seriesDescription: file ? imageNameOf(file.name, file.slide) : '',
    // An X-ray's header settles it (dicomSlides isXray); the server writes DX from it.
    imageType: certainType ?? defaultImageType(kind),
    specimenTypeValue: '119376003',
    specimenTypeScheme: 'SCT',
    specimenTypeMeaning: 'tissue specimen',
  };
}

/**
 * The form after a file said what it is (a DICOM header, a metadata file):
 * a modality it brings that the form did not have sets the Type that modality
 * says — the file knows what it is, where the form's Type was only its
 * extension's guess, and the server writes the modality from the Type. DICOM
 * writes a whole-slide image as SM, so SM says a slide; a modality none of the
 * seven fit leaves the Type unsaid ("Other"), and the modality stands. A Type
 * the file certainly cannot be (a scanner's slide said XC) is not taken.
 * `after` itself when the Type stays. A file certain of its Type (an X-ray)
 * keeps it whatever modality it says: a Kubtec cabinet writes its X-rays as SM.
 */
export function followType(before: MetadataDraft, after: MetadataDraft, certain: ImageKind | null, certainType: ImageType | null = null): MetadataDraft {
  if (certainType) return after.imageType === certainType ? after : { ...after, imageType: certainType };
  const m = after.modality.trim().toUpperCase();
  if (!m || m === before.modality.trim().toUpperCase()) return after;
  const t = imageTypeOf(m, after.illuminationValue.trim() || null, m === 'SM' ? 'wsi' : 'flat') ?? '';
  if (t && !typeFits(t, certain)) return after;
  return t === after.imageType ? after : { ...after, imageType: t };
}

export type ImageKey =
  | 'image_type'
  | 'modality'
  | 'description'
  | 'specimen_short_description'
  | 'site'
  | 'laterality'
  | 'objective_power'
  | 'specimen_type'
  | 'procedure'
  | 'container_identifier'
  | 'container_issuer'
  | 'specimen_identifier'
  | 'manufacturer'
  | 'station_name'
  | 'device_serial';

export type StudyGroup = 'study_id' | 'description' | 'sex' | 'age' | 'referring_physician' | 'study_datetime' | 'accession' | 'patient';

export interface AccessionGroup {
  number: string;
  issuer: string | null;
}

/**
 * The details as groups. An ABSENT group is unknown: in a wire built from the
 * form, one the form cannot express yet (see `problems`); in what the server
 * took, one never sent.
 */
export interface Wire {
  image: Partial<Record<ImageKey, unknown>>;
  study: Partial<Record<StudyGroup, unknown>>;
}

/** A form field the server would refuse, said before it is sent. */
export interface FieldProblem {
  field: keyof MetadataDraft;
  message: string;
}

// The server's limits (drafts.ts): DICOM LO is 64, SH 16.
const MODALITY = /^[A-Z]{2,16}$/;
const CODE_VALUE = /^[A-Za-z0-9._:-]{1,64}$/;
const SCHEME = /^[A-Za-z0-9._-]{1,16}$/;
const AGE = /^(\d{1,3})\s*([DWMY+])?$/i;

/**
 * Form text to its value: trimmed, empty is null. A problem when it will not
 * fit or holds what DICOM text cannot (a backslash, control characters).
 */
function textOf(v: string, max: number, field: keyof MetadataDraft, problems: FieldProblem[], label = 'This'): string | null | undefined {
  const t = v.trim();
  if (!t) return null;
  if (t.length > max) {
    problems.push({ field, message: `${label} is limited to ${max} characters.` });
    return undefined;
  }
  // eslint-disable-next-line no-control-regex
  if (/[\\\x00-\x1f]/.test(t)) {
    problems.push({ field, message: `${label} cannot contain a backslash.` });
    return undefined;
  }
  return t;
}

/** "Family, Given" (or DICOM "Family^Given") → "Family^Given"; empty is null. */
export function personName(family: string, given: string): string | null {
  const f = family.trim();
  const g = given.trim();
  if (!f && !g) return null;
  return g ? `${f}^${g}` : f;
}

/** The referring physician field, "Last, First" or already "Last^First". */
export function referringPn(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  if (t.includes('^')) return t;
  if (t.includes(',')) {
    const [family, given] = t.split(',', 2).map((s) => s.trim());
    return personName(family, given);
  }
  return t;
}

function codedOf(
  d: MetadataDraft,
  keys: [keyof MetadataDraft, keyof MetadataDraft, keyof MetadataDraft],
  label: string,
  problems: FieldProblem[],
): CodedValue | null | undefined {
  const [code, scheme, meaning] = keys.map((k) => d[k].trim());
  if (!code && !scheme && !meaning) return null;
  if (!CODE_VALUE.test(code)) {
    problems.push({ field: keys[0], message: code ? `${label}: the code may hold only letters, digits and . _ : -` : `${label} needs a code.` });
    return undefined;
  }
  if (!SCHEME.test(scheme)) {
    problems.push({ field: keys[0], message: scheme ? `${label}: the coding scheme is not valid.` : `${label} needs a coding scheme (e.g. SCT or DCM).` });
    return undefined;
  }
  if (meaning.length > 64 || /[\\]/.test(meaning)) {
    problems.push({ field: keys[0], message: `${label}: the meaning is limited to 64 characters, without a backslash.` });
    return undefined;
  }
  return { code, scheme, meaning };
}

/**
 * PatientAge as the server takes it, in one spelling (`54Y`, `8M`): the form
 * says 54, 54Y or 054Y for the same age, and the server stores `054Y`, so a
 * save measured against what it stored must not see a change in the spelling.
 */
function ageOf(v: string, problems: FieldProblem[]): string | null | undefined {
  const t = v.trim();
  if (!t) return null;
  const m = AGE.exec(t);
  if (!m) {
    problems.push({ field: 'patientAge', message: 'Give the age as 54, 54Y, 8M or 90+.' });
    return undefined;
  }
  const unit = !m[2] || m[2] === '+' ? 'Y' : m[2].toUpperCase();
  return `${Number(m[1])}${unit}`;
}

/** A stored age (`054Y`, or the canonical `54Y`) as the form shows it: years plain, other units spelled. */
function ageField(v: string | null): string {
  const m = v ? /^(\d{1,3})([DWMY])?$/.exec(v.trim().toUpperCase()) : null;
  if (!m) return v ?? '';
  return !m[2] || m[2] === 'Y' ? String(Number(m[1])) : `${Number(m[1])}${m[2]}`;
}

/** The form as groups, and what in it the server would refuse. */
export function wireOf(d: MetadataDraft): { wire: Wire; problems: FieldProblem[] } {
  const problems: FieldProblem[] = [];
  const image: Wire['image'] = {};
  const study: Wire['study'] = {};
  const put = <K extends string>(into: Partial<Record<K, unknown>>, k: K, v: unknown) => {
    if (v !== undefined) into[k] = v;
  };

  // ── the image ──
  const type = d.imageType.trim();
  if (!type) image.image_type = null;
  else if (isImageType(type)) image.image_type = type;
  else problems.push({ field: 'imageType', message: 'Choose one of the types offered.' });
  // Only what a file said (a DICOM header, a metadata sidecar): the server writes the modality from the Type otherwise.
  const modality = d.modality.trim().toUpperCase();
  if (modality && MODALITY.test(modality)) image.modality = modality;
  else if (modality) problems.push({ field: 'modality', message: 'A modality is a DICOM code such as SM or XC.' });
  put(image, 'description', textOf(d.seriesDescription, 64, 'seriesDescription', problems, 'The name'));
  put(image, 'specimen_short_description', textOf(d.specimenDescription, 64, 'specimenDescription', problems, 'The specimen description'));
  put(image, 'site', codedOf(d, ['anatomicSiteValue', 'anatomicSiteScheme', 'anatomicSiteMeaning'], 'The anatomic site', problems));
  // The side, said in any order with the site: the file carries it once there is one.
  const laterality = d.laterality.trim();
  if (!laterality) image.laterality = null;
  else if (isLaterality(laterality)) image.laterality = laterality;
  else problems.push({ field: 'laterality', message: 'A laterality is Left, Right or Bilateral.' });
  // Only what a companion file said: the form has no field for it, and never
  // clears the power ingest reads from the scanner's file. One that is no
  // magnification (1 to 200) is left out, not held against a field not drawn.
  const power = Number(d.objectivePower.trim().replace(/x$/i, ''));
  if (power > 0 && power <= 200) image.objective_power = power;
  put(image, 'specimen_type', codedOf(d, ['specimenTypeValue', 'specimenTypeScheme', 'specimenTypeMeaning'], 'The specimen type', problems));
  put(image, 'procedure', codedOf(d, ['procedureValue', 'procedureScheme', 'procedureMeaning'], 'The procedure', problems));
  put(image, 'container_identifier', textOf(d.containerId, 64, 'containerId', problems, 'The container ID'));
  put(image, 'container_issuer', textOf(d.containerIdIssuer, 64, 'containerIdIssuer', problems, 'The issuer'));
  put(image, 'specimen_identifier', textOf(d.specimenId, 64, 'specimenId', problems, 'The specimen ID'));
  put(image, 'manufacturer', textOf(d.manufacturer, 64, 'manufacturer', problems, 'The manufacturer'));
  put(image, 'station_name', textOf(d.stationName, 16, 'stationName', problems, 'The station name'));
  put(image, 'device_serial', textOf(d.deviceSerial, 64, 'deviceSerial', problems, 'The serial number'));

  // ── the study (the case) ──
  put(study, 'study_id', textOf(d.studyId, 16, 'studyId', problems, 'The study ID'));
  put(study, 'description', textOf(d.studyDescription, 64, 'studyDescription', problems, 'The description'));
  study.sex = d.patientSex || null;
  put(study, 'age', ageOf(d.patientAge, problems));
  const referring = referringPn(d.referringPhysician);
  if (referring && referring.length > 64) problems.push({ field: 'referringPhysician', message: 'The name is limited to 64 characters.' });
  else study.referring_physician = referring;
  study.study_datetime = d.studyDateTime.trim() || null;

  const number = textOf(d.accession, 16, 'accession', problems, 'The accession number');
  const accIssuer = textOf(d.accessionIssuer, 64, 'accessionIssuer', problems, 'The issuer');
  if (number !== undefined && accIssuer !== undefined) {
    study.accession = number ? ({ number, issuer: accIssuer } satisfies AccessionGroup) : null;
  }

  const id = textOf(d.patientId, 64, 'patientId', problems, 'The patient ID');
  const issuer = textOf(d.patientIdIssuer, 64, 'patientIdIssuer', problems, 'The issuer');
  const name = personName(d.patientFamily, d.patientGiven);
  const birth = d.patientBirthDate.trim() || null;
  if (name && name.length > 64) problems.push({ field: 'patientFamily', message: 'The name is limited to 64 characters.' });
  else if (id !== undefined && issuer !== undefined) {
    if (id) study.patient = { id, issuer, name, birth_date: birth } satisfies PatientDetails;
    else if (name || birth || issuer) problems.push({ field: 'patientId', message: 'A patient ID is needed to record the patient’s name, birth date or issuer.' });
    else study.patient = null;
  }
  return { wire: { image, study }, problems };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** The groups of `now` that `saved` does not have as they are: what a save sends. */
export function deltaOf(now: Wire, saved: Wire | null): Wire {
  const pick = <K extends string>(n: Partial<Record<K, unknown>>, s: Partial<Record<K, unknown>> | undefined) =>
    Object.fromEntries(Object.entries(n).filter(([k, v]) => !s || !(k in s) || !same(v, s[k as K]))) as Partial<Record<K, unknown>>;
  return { image: pick(now.image, saved?.image), study: pick(now.study, saved?.study) };
}

export const isEmptyWire = (w: Wire) => Object.keys(w.image).length === 0 && Object.keys(w.study).length === 0;

/** The image groups as PATCH /images/:id takes them. */
export function imageBody(image: Wire['image']): EnterpriseImageDetails {
  return { ...image } as EnterpriseImageDetails;
}

/** The study groups as PATCH /images/:id/study takes them: the accession and the patient spread into their keys. */
export function studyBody(study: Wire['study']): CaseDetails {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(study)) {
    if (k === 'accession') {
      const a = v as AccessionGroup | null;
      out.accession_number = a?.number ?? null;
      out.accession_issuer = a?.issuer ?? null;
    } else out[k] = v;
  }
  return out as CaseDetails;
}

/**
 * The create's body, from the form: every image group (a new row has nothing
 * to keep but the server's defaults, which the form shows), and only the study
 * groups that SAY something — an accession may name a case that exists, and
 * an empty field must not clear what that case already has.
 */
export function createDetails(wire: Wire): { image: EnterpriseImageDetails; study: CaseDetails } {
  const said = Object.fromEntries(Object.entries(wire.study).filter(([, v]) => v !== null)) as Wire['study'];
  return { image: imageBody(wire.image), study: studyBody(said) };
}

/** The form fields a group fills. The objective power fills none: the form never shows it. */
const FIELDS_OF_IMAGE: Record<ImageKey, (keyof MetadataDraft)[]> = {
  image_type: ['imageType'],
  modality: ['modality'],
  description: ['seriesDescription'],
  specimen_short_description: ['specimenDescription'],
  site: ['anatomicSiteValue', 'anatomicSiteScheme', 'anatomicSiteMeaning'],
  laterality: ['laterality'],
  objective_power: [],
  specimen_type: ['specimenTypeValue', 'specimenTypeScheme', 'specimenTypeMeaning'],
  procedure: ['procedureValue', 'procedureScheme', 'procedureMeaning'],
  container_identifier: ['containerId'],
  container_issuer: ['containerIdIssuer'],
  specimen_identifier: ['specimenId'],
  manufacturer: ['manufacturer'],
  station_name: ['stationName'],
  device_serial: ['deviceSerial'],
};
const FIELDS_OF_STUDY: Record<StudyGroup, (keyof MetadataDraft)[]> = {
  study_id: ['studyId'],
  description: ['studyDescription'],
  sex: ['patientSex'],
  age: ['patientAge'],
  referring_physician: ['referringPhysician'],
  study_datetime: ['studyDateTime'],
  accession: ['accession', 'accessionIssuer'],
  patient: ['patientId', 'patientIdIssuer', 'patientFamily', 'patientGiven', 'patientBirthDate'],
};

/** What the image summary carries when the server could not build the rest (`details` null). */
const SUMMARY_GROUPS: { image: ImageKey[]; study: StudyGroup[] } = {
  image: ['image_type', 'modality', 'description', 'specimen_short_description', 'site', 'laterality'],
  study: ['study_id', 'description'],
};

/**
 * Take what the server says an image is into a row's form, group by group,
 * where the row has nothing of its own to say: a group is taken only when the
 * form still says exactly what the server last took (`saved`), and the server
 * now says otherwise — the accession moved the slide into a case that has its
 * patient, another tab changed it.
 * Returns the new form and what the server took, or null when nothing changed.
 */
export function adoptView(draft: MetadataDraft, saved: Wire, v: EnterpriseImage): { draft: MetadataDraft; saved: Wire } | null {
  const shown = draftFromView(v);
  const listed = wireOf(shown.draft).wire;
  const now = wireOf(draft).wire;
  let next = draft;
  const nextSaved: Wire = { image: { ...saved.image }, study: { ...saved.study } };
  let changed = false;
  const take = <K extends string>(part: 'image' | 'study', fieldsOf: Record<K, (keyof MetadataDraft)[]>, only: K[] | null) => {
    for (const [g, value] of Object.entries(listed[part]) as [K, unknown][]) {
      if (only && !only.includes(g)) continue;
      const n = (now[part] as Record<string, unknown>)[g];
      const s = (saved[part] as Record<string, unknown>)[g];
      if (n === undefined || s === undefined || !same(n, s) || same(s, value)) continue;
      next = { ...next, ...Object.fromEntries(fieldsOf[g].map((f) => [f, shown.draft[f]])) };
      (nextSaved[part] as Record<string, unknown>)[g] = value;
      changed = true;
    }
  };
  take<ImageKey>('image', FIELDS_OF_IMAGE, shown.partial ? SUMMARY_GROUPS.image : null);
  take<StudyGroup>('study', FIELDS_OF_STUDY, shown.partial ? SUMMARY_GROUPS.study : null);
  return changed ? { draft: next, saved: nextSaved } : null;
}

/**
 * The form for an image as the server has it: its summary and every detail
 * written about it, for a row this page did not make (the list's history,
 * another session) — so a patient or an accession edited there is sent with
 * the issuer and birth date the server already has, never empty ones. `saved`
 * is what that form says: nothing is owed until the person changes something.
 * `partial` when the server could not say more than the summary.
 */
export function draftFromView(v: EnterpriseImage): { draft: MetadataDraft; saved: Wire; partial: boolean } {
  const d: MetadataDraft = { ...EMPTY_DRAFT, imageType: isImageType(v.image_type) ? v.image_type : '', modality: v.modality ?? '', laterality: lateralityOfCode(v.laterality?.code) ?? '' };
  d.seriesDescription = v.description ?? '';
  d.specimenDescription = v.specimen_short_description ?? '';
  d.anatomicSiteValue = v.site?.code ?? '';
  d.anatomicSiteScheme = v.site?.scheme ?? '';
  d.anatomicSiteMeaning = v.site?.meaning ?? '';
  if (v.study) {
    d.studyId = v.study.study_id ?? '';
    d.studyDescription = v.study.description ?? '';
    d.accession = v.study.accession_number ?? '';
    d.patientId = v.study.patient_id ?? '';
    [d.patientFamily, d.patientGiven] = v.study.patient_name ? splitPn(v.study.patient_name) : ['', ''];
  }
  const w = v.details;
  if (w) {
    const coded = (c: CodedValue | null, keys: [keyof MetadataDraft, keyof MetadataDraft, keyof MetadataDraft]) =>
      Object.assign(d, { [keys[0]]: c?.code ?? '', [keys[1]]: c?.scheme ?? '', [keys[2]]: c?.meaning ?? '' });
    coded(w.image.illumination, ['illuminationValue', 'illuminationScheme', 'illuminationMeaning']);
    coded(w.image.specimen_type, ['specimenTypeValue', 'specimenTypeScheme', 'specimenTypeMeaning']);
    coded(w.image.procedure, ['procedureValue', 'procedureScheme', 'procedureMeaning']);
    d.containerId = w.image.container_identifier ?? '';
    d.containerIdIssuer = w.image.container_issuer ?? '';
    d.specimenId = w.image.specimen_identifier ?? '';
    d.manufacturer = w.image.manufacturer ?? '';
    d.stationName = w.image.station_name ?? '';
    d.deviceSerial = w.image.device_serial ?? '';
    d.patientSex = w.study.sex ?? '';
    d.patientAge = ageField(w.study.age);
    d.referringPhysician = studyGroupFields('referring_physician', w.study.referring_physician).referringPhysician ?? '';
    d.studyDateTime = studyDateTimeField(w.study.study_date, w.study.study_time);
    d.accessionIssuer = v.study?.accession_number ? w.study.accession_issuer ?? '' : '';
    d.patientIdIssuer = v.study?.patient_id ? w.study.patient_issuer ?? '' : '';
    d.patientBirthDate = v.study?.patient_id ? w.study.patient_birth_date ?? '' : '';
  }
  return { draft: d, saved: wireOf(d).wire, partial: !w };
}

/** A stored study date (`YYYY-MM-DD`) and time (DICOM TM, `HHMMSS`) as the form's date & time field. */
export function studyDateTimeField(date: string | null, time: string | null): string {
  if (!date) return '';
  return time && /^\d{2}/.test(time) ? `${date}T${time.slice(0, 2)}:${time.slice(2, 4).padEnd(2, '0')}` : date;
}

/** The form fields a study group's value fills: the inverse of wireOf for the study. */
export function studyGroupFields(g: StudyGroup, value: unknown): Partial<MetadataDraft> {
  switch (g) {
    case 'study_id':
      return { studyId: (value as string | null) ?? '' };
    case 'description':
      return { studyDescription: (value as string | null) ?? '' };
    case 'sex':
      return { patientSex: (value as MetadataDraft['patientSex'] | null) ?? '' };
    case 'age':
      return { patientAge: ageField((value as string | null) ?? null) };
    case 'referring_physician': {
      const [family, given] = value ? splitPn(value as string) : ['', ''];
      return { referringPhysician: given ? `${family}, ${given}` : family };
    }
    case 'study_datetime':
      return { studyDateTime: (value as string | null) ?? '' };
    case 'accession': {
      const a = value as AccessionGroup | null;
      return { accession: a?.number ?? '', accessionIssuer: a?.issuer ?? '' };
    }
    case 'patient': {
      const p = value as PatientDetails | null;
      const [family, given] = p?.name ? splitPn(p.name) : ['', ''];
      return { patientId: p?.id ?? '', patientIdIssuer: p?.issuer ?? '', patientFamily: family, patientGiven: given, patientBirthDate: p?.birth_date ?? '' };
    }
  }
}

/**
 * A study save of one slide, told to another slide of the same case: each
 * group the other row has nothing of its own to say about (its form still
 * says what the server last took) takes the saved value. Null when none does.
 */
export function shareStudy(draft: MetadataDraft, saved: Wire, groups: Wire['study']): { draft: MetadataDraft; saved: Wire } | null {
  const now = wireOf(draft).wire.study;
  let next = draft;
  const nextStudy = { ...saved.study };
  let changed = false;
  for (const [g, value] of Object.entries(groups) as [StudyGroup, unknown][]) {
    if (g === 'accession') continue; // the same case already; a move is the moved slide's own
    if (!(g in now) || !(g in saved.study) || !same(now[g], saved.study[g]) || same(saved.study[g], value)) continue;
    next = { ...next, ...studyGroupFields(g, value) };
    nextStudy[g] = value;
    changed = true;
  }
  return changed ? { draft: next, saved: { image: saved.image, study: nextStudy } } : null;
}

/** The form field a server refusal ("invalid <key>") is about. */
export function fieldOfServerKey(key: string, part: 'image' | 'study'): keyof MetadataDraft | null {
  if (part === 'image' && key in FIELDS_OF_IMAGE) return FIELDS_OF_IMAGE[key as ImageKey][0] ?? null;
  if (part === 'study') {
    if (key === 'accession_number') return 'accession';
    if (key === 'accession_issuer') return 'accessionIssuer';
    if (key.startsWith('patient')) {
      const sub = key.split('.')[1];
      return sub === 'issuer' ? 'patientIdIssuer' : sub === 'name' ? 'patientFamily' : sub === 'birth_date' ? 'patientBirthDate' : 'patientId';
    }
    if (key in FIELDS_OF_STUDY) return FIELDS_OF_STUDY[key as StudyGroup][0];
  }
  return null;
}
